import { App, ConfigProvider } from 'antd';

import nlNL from 'antd/locale/nl_NL';

import { MsalProvider } from '@azure/msal-react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { FC, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { UserProvider } from './lib/context/userContext';
import { httpErrorConverter, msalInstance } from './lib/utilities';
import theme from './theme';
import { ValidationError } from './shared/types/general';
import { FeatureFlagProvider } from './lib/context/featureFlagContext';

export const Providers: React.FunctionComponent<PropsWithChildren> = ({ children }) => {
  const { message: messageApi } = App.useApp();
  const { t } = useTranslation('errors');

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
      },
    },
    queryCache: new QueryCache({
      onError: async (error, query) => {
        if (query.meta?.errorMessage) {
          messageApi.error(query.meta.errorMessage as string);
        } else {
          const message = await httpErrorConverter(error);
          messageApi.error(<ErrorMessage errorContext={message} />);
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: async (error, variables, context, mutation) => {
        if (mutation.meta?.errorMessage) {
          messageApi.error(t(mutation.meta.errorMessage as string));
        } else {
          const message = await httpErrorConverter(error);
          messageApi.error(<ErrorMessage errorContext={message} />);
        }
      },
    }),
  });

  const ErrorMessage: FC<{ errorContext: ValidationError[] | string }> = ({ errorContext }) => {
    if (typeof errorContext == 'string') {
      return errorContext;
    }
    return (
      <div>
        {errorContext.map((err, ix) => (
          <div key={`validation-error-${ix}`}>{t(err.errorCode, { ...err.properties, defaultValue: err.message }).toString()}</div>
        ))}
      </div>
    );
  };

  return (
    <MsalProvider instance={msalInstance}>
      <FeatureFlagProvider>
        <UserProvider>
          <ConfigProvider locale={nlNL} theme={theme}>
            <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
          </ConfigProvider>
        </UserProvider>
      </FeatureFlagProvider>
    </MsalProvider>
  );
};